// Task List V2 — the shape bridge. The grid renders two different upstream
// shapes interchangeably:
//   - the REAL enriched API feed (M1): { seq, mission_phase, subtask_count,
//     time_logged_s, due_on, depends_on, … }
//   - the MOCK fixtures (_mock/sampleData.js): { key, phase, progress,
//     logged_seconds, parent_id, estimated_hours, reporter_id, created_at, … }
// Both are normalized here to ONE canonical row so every column renderer,
// selector and editor downstream is shape-agnostic. Missing fields degrade to
// sensible blanks (— in the cell) rather than throwing.

import { membersById } from '@/components/project-view/_mock/sampleData.js';

/** Seconds → whole-ish hours, one decimal. */
function secondsToHours(seconds) {
  if (!seconds) return 0;
  return Math.round((seconds / 3600) * 10) / 10;
}

/** Resolve a reporter id to a display name via the mock member table. */
function reporterName(reporterId) {
  if (!reporterId) return null;
  return membersById[reporterId]?.display_name ?? reporterId;
}

/**
 * Normalize one upstream story (API or mock) to the canonical grid row.
 * The canonical shape is a superset; absent source fields become null/0.
 */
export function normalizeStory(s) {
  return {
    id: s.id,
    // Key: API rows carry `seq` (→ #N), mock rows carry a human `key` (TES-N).
    key: s.key ?? (s.seq != null ? `#${s.seq}` : s.id),
    seq: s.seq ?? null,
    title: s.title ?? '',
    description:
      s.description ?? s.description_md ?? '',
    status: s.status ?? 'backlog',
    priority: s.priority ?? null,
    progress:
      s.progress != null ? s.progress : s.status === 'done' ? 100 : 0,
    phase: s.mission_phase ?? s.phase ?? null,
    assignees: s.assignees ?? [],
    labels: s.labels ?? [],
    startDate: s.start_date ?? null,
    dueOn: s.due_on ?? null,
    doneAt: s.done_at ?? null,
    estimatedHours: s.estimated_hours ?? null,
    // Time logged in hours, derived from whichever field is present.
    loggedHours:
      s.logged_seconds != null
        ? secondsToHours(s.logged_seconds)
        : s.time_logged_s != null
          ? secondsToHours(s.time_logged_s)
          : 0,
    loggedSeconds: s.logged_seconds ?? s.time_logged_s ?? 0,
    reporter: reporterName(s.reporter_id),
    createdAt: s.created_at ?? null,
    updatedAt: s.updated_at ?? null,
    parentId: s.parent_id ?? null,
    dependsOn: s.depends_on ?? [],
    // Git-graph lane hint (office home's story graph) — pass through if present.
    branch: s.branch,
    // subtask_count is precomputed on the API feed; for mock rows it's derived
    // by the hook (which has the full list to count children).
    subtaskCount: s.subtask_count ?? 0,
    missionId: s.mission_id ?? null,
    missionTitle: s.mission_title ?? null,
    // Group of a top-level mission (home graph view's office→group→mission tree).
    group: s.group ?? null,
    // The mission's group (product), for the Mission / Group grid column.
    productName: s.product_name ?? null,
    productColor: s.product_color ?? null,
    // Cross-project context (office home's "my stories"): the list view shows a
    // project chip when present. Project-scoped feeds never set these.
    project: s.project ?? null,
    projectColor: s.projectColor ?? s.project_color ?? null,
    due_on: s.due_on ?? null,
    // Shaping : the story belongs to a set the POC has not approved yet.
    draft: Boolean(s.draft),
    // Keep the raw row so editors can fall back to source-specific fields.
    __raw: s,
  };
}

/**
 * Normalize a list, computing subtask counts for mock rows (which expose
 * parent_id instead of a precomputed count). Returns top-level rows only;
 * children are looked up on demand via childrenOf().
 */
export function normalizeList(stories) {
  const rows = stories.map(normalizeStory);
  // Derive subtask counts from parent_id when the source didn't provide one.
  const childCount = new Map();
  for (const r of rows) {
    if (r.parentId) childCount.set(r.parentId, (childCount.get(r.parentId) ?? 0) + 1);
  }
  for (const r of rows) {
    if (!r.subtaskCount && childCount.has(r.id)) r.subtaskCount = childCount.get(r.id);
  }
  return rows;
}

/** Top-level rows (no parent) — the grid roots. */
export function topLevel(rows) {
  return rows.filter((r) => !r.parentId);
}

/** Direct children of a row id. */
export function childrenOf(rows, parentId) {
  return rows.filter((r) => r.parentId === parentId);
}
